import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";
import clsx from "clsx";

import { VOTE_ITEM_TYPE, VOTE_STATUS } from "../../../../consts/hotColdVote";
import { INCOMMODITY_SATISFACTION_MANAGER } from "../../../../consts/incommodity";
import { SERVICE_TEMPLATE_ID } from "../../../../consts/serviceTemplate";

import useErrors from "../../../../libs/useErrors";
import { getLocalValue, setLocalValue } from "../../../../libs/localStorage";
import { errorResponse } from "../../../../libs/common";
import { getDateString, isTimePassed } from "../../../../libs/dateFormat";
import { safeReplaceAll } from "../../../../libs/replace";

import { BuildingVocCategorySetting } from "../../../../types/hotCold";

import { useUserContext } from "../../../../contexts/userContext";

import {
  getBuildingVocCategorySetting,
  getHotColdVote,
  getHotColdVoteStatus,
  getVoteItemTypeLabel,
  getVoteStatusLabel,
  HotColdCreateRequest,
  isWeekendOrAfter6PM,
  postHotColdVote,
  postHotColdVoteItem,
  Vote,
  VoteItemType,
  VoteRecentInfo,
  VoteSpace,
  VoteStatus,
} from "../../../../services/hotColdVoteServices";
import {
  BuildingFloorItem,
  BuildingSpaceDetail,
  BuildingSpaceItem,
  getBuildingFloorList,
  getBuildingSpaceDetail,
  getBuildingSpaceList,
} from "../../../../services/buildingServices";
import {
  getHotColdNotice,
  TalkHotColdItem,
} from "../../../../services/talkServices";
import { SatisfactionRequest } from "../../../../services/userReportServices";
import {
  getHolidayCheck,
  patchEtcSatisfaction,
  postEtcSatisfaction,
} from "../../../../services/etcServices";

import useSimplePopup from "../../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";
import { RefreshIcon } from "../../../../hooks/useStoreHeader";

import StickyHeader from "../../../../components/Layout/StickyHeader";
import CommonSelect from "../../../../components/Select";
import CommonPopup from "../../../../components/Popup";
import CommonInput from "../../../../components/Input/CommonInput";
import CommonButton from "../../../../components/Button";
import PinchZoomImage from "../../../../components/PinchZoomImage";
import HotColdSummaryList from "../../../../components/HotCold/HotColdSummaryList";
import UserSatisfactionPopup from "../../../../components/Popup/UserSatisfaction";
import HorizontalRule from "../../../../components/Rule/Horizontal";
import { HotColdVoteRemainTime } from "../../../../components/HotCold/RemainTime";
import HistorySatisfaction, {
  SatisfactionReplyItem,
} from "../../../../components/History/Satisfaction";
import HotColdVoteEndPopup from "../../../../components/Popup/HotColdVoteEnd";
import HotColdVoteOnGoingPopup from "../../../../components/Popup/HotColdVoteOnGoing";
import HotColdVoteReportPopup from "../../../../components/Popup/HotColdVoteReport";
import { HotColdVoteBarChart } from "../../../../components/HotCold/BarChart";
import {
  HomeBuildingNoticeIcon,
  RemainBalanceRightArrow,
  ViewNoticeLinkIcon,
} from "../../../View";
import { Skeleton, TalkFindBuildingDownArrow } from "../../../Talk";

import { VoteIcon } from "..";
import YesNo from "../../../../assets/img/vote-yes-no.png";
import YesNoOngoing from "../../../../assets/img/vote-yes-no-ongoing.png";
import VoteHot from "../../../../assets/img/vote-hot.png";
import VoteCold from "../../../../assets/img/vote-cold.png";
import VoteGood from "../../../../assets/img/vote-good.png";

type Props = {};

type PopupType =
  | "selectFloor"
  | "selectSpace"
  | "voteCreate"
  | "voting"
  | "satisfaction"
  | "differentBuilding"
  | "onGoing"
  | "voteEnd"
  | "report"
  | "";

type LoadVoteParams = {
  vote: Partial<Vote>;
  showIsLoading?: boolean;
};

export default function HotColdVoteDetail(props: Props) {
  const selectFloorRef = useRef<HTMLDivElement>(null);
  const selectSpaceRef = useRef<HTMLDivElement>(null);
  const inputLocationRef = useRef<HTMLInputElement>(null);

  const VOTE_DURATION = 10; // 투표 시간(분)) 설정

  const navigate = useNavigate();
  const location = useLocation();

  const [errors, { removeError, setError, clearErrors }] = useErrors();
  const [user] = useUserContext();
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isBuildingLoading, setIsBuildingLoading] = useState<boolean>(true);
  const [isVoteLoading, setIsVoteLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({
    isLoading: isLoading || isBuildingLoading || isVoteLoading,
    testId: "hot-cold-vote-detail",
  });

  const [popup, setPopup] = useState<PopupType>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedVote, setSelectedVote] = useState<VoteItemType | undefined>(
    undefined
  );
  const [vote, setVote] = useState<Partial<Vote>>({});

  const [hotColdNotice, setHotColdNotice] = useState<TalkHotColdItem>();
  const [spaceDetail, setSpaceDetail] = useState<
    BuildingSpaceDetail | undefined
  >(undefined);
  const [voteSpace, setVoteSpace] = useState<VoteSpace | undefined>(undefined);

  const [floorList, setFloorList] = useState<BuildingFloorItem[]>([]);
  const [spaceList, setSpaceList] = useState<BuildingSpaceItem[]>([]);
  const [selectedFloor, setSelectedFloor] = useState<
    BuildingFloorItem | undefined
  >();
  const [selectedSpace, setSelectedSpace] = useState<
    BuildingSpaceItem | undefined
  >();

  const [recentLocation, setRecentLocation] = useState<
    VoteRecentInfo | undefined
  >(undefined);
  const [locationIndex, setLocationIndex] = useState<number>(-1);
  const [categorySetting, setCategorySetting] = useState<
    BuildingVocCategorySetting | undefined
  >(undefined);

  const params = new URLSearchParams(location.search);
  const paramVoteId = Number(params.get("voteId"));
  const paramBuildingId = params.get("buildingId") ?? user.buildingId ?? "";
  const paramSpaceId = params.get("buildingSpaceId");

  const getTextColor = (voteItemType?: VoteItemType) => {
    if (!voteItemType) return "";

    return voteItemType === VOTE_ITEM_TYPE.HOT
      ? "text-[#EB3F59]"
      : voteItemType === VOTE_ITEM_TYPE.COLD
      ? "text-[#0A78F2]"
      : "text-[#3C6578]";
  };

  const getResultString = () => {
    if (!vote.summary) return "";
    const { hotCount, coldCount, goodCount } = vote.summary;
    const maxCount = Math.max(hotCount, coldCount, goodCount);

    if (
      maxCount === hotCount &&
      hotCount === coldCount &&
      hotCount >= goodCount
    ) {
      return "덥다는 의견과 춥다는 의견이 반반이에요!";
    } else {
      if (maxCount === hotCount) {
        return `덥다는 의견이 더 많았습니다.`;
      } else if (maxCount === coldCount) {
        return `춥다는 의견이 더 많았습니다.`;
      } else if (maxCount === goodCount) {
        return `괜찮다는 의견이 더 많았습니다.`;
      } else {
        return "";
      }
    }
  };

  const validationVoteCreate = () => {
    if (selectedFloor === undefined) {
      setError("selectFloor", "층을 선택해주세요");
      selectFloorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return false;
    }

    if (!selectedSpace) {
      setError("selectSpace", "상세공간을 선택해주세요");
      selectSpaceRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return false;
    }

    if (!vote.location) {
      setError("inputLocation", "세부위치정보를 입력해주세요");
      inputLocationRef.current?.focus();
      return false;
    }

    return true;
  };

  const loadNotice = async () => {
    try {
      const res = await getHotColdNotice({ buildingId: paramBuildingId });
      if (res.status === HttpStatusCode.Ok) {
        setHotColdNotice(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const loadFloor = async () => {
    if (!paramBuildingId) return;

    try {
      const resFloor = await getBuildingFloorList({
        buildingId: paramBuildingId,
      });
      if (resFloor.status === HttpStatusCode.Ok) {
        setFloorList(resFloor.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const loadCategorySetting = async () => {
    if (!paramBuildingId) return;

    try {
      const res = await getBuildingVocCategorySetting({
        buildingId: paramBuildingId,
      });
      if (res.status === HttpStatusCode.Ok) {
        setCategorySetting(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const loadSpaceDetail = async (
    buildingSpaceId: string,
    callback?: (params: LoadVoteParams) => Promise<void>
  ) => {
    if (buildingSpaceId === "") return;

    setIsLoading(true);
    Promise.allSettled([
      getBuildingSpaceDetail({ buildingSpaceId }),
      getHotColdVoteStatus({ buildingSpaceId }),
    ])
      .then((ress) => {
        if (
          ress[0].status === "fulfilled" &&
          ress[0].value.status === HttpStatusCode.Ok
        ) {
          setSpaceDetail(ress[0].value.data);
        }
        if (
          ress[1].status === "fulfilled" &&
          ress[1].value.status === HttpStatusCode.Ok
        ) {
          setVoteSpace(ress[1].value.data);
          callback?.({
            vote: {
              voteId: ress[1].value.data.vote?.voteId,
              ...ress[1].value.data,
            },
          });
        }
      })
      .catch((e: any) => {
        errorResponse(
          e,
          open,
          "조회 중 오류가 발생했습니다.\n관리자에게 문의해주세요."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const loadVote = async ({ vote, showIsLoading = true }: LoadVoteParams) => {
    if (showIsLoading) setIsLoading(true);

    try {
      // 투표 존재 시 상세 조회
      if (vote?.voteId) {
        const res = await getHotColdVote({ voteId: vote.voteId });
        if (res.status === HttpStatusCode.Ok) {
          const {
            userVoteItem,
            buildingFloorId,
            buildingFloorName: floorName,
            buildingSpaceId,
            buildingSpaceName: spaceName,
          } = res.data;

          setVote(res.data);
          setSelectedVote(userVoteItem?.itemType);
          setSelectedFloor({
            buildingFloorId,
            floorName,
          } as BuildingFloorItem);
          setSelectedSpace({
            buildingSpaceId,
            spaceName,
          } as BuildingSpaceItem);
        }
      } else {
        setSelectedFloor({
          buildingFloorId: vote.buildingFloorId ?? user.buildingFloorId ?? "",
          floorName: vote.buildingFloorName ?? user.buildingFloorName ?? "",
        } as BuildingFloorItem);
        setSelectedSpace({
          buildingSpaceId: vote.buildingSpaceId ?? user.buildingSpaceId ?? "",
          spaceName: vote.buildingSpaceName ?? user.buildingSpaceName ?? "",
        } as BuildingSpaceItem);
      }
    } catch (e) {
      console.log(e);
    } finally {
      if (showIsLoading) setIsLoading(false);
    }
  };

  const getRecentLocation = () => {
    const locationList = getLocalValue("recentLocation");
    if (locationList) {
      const list = JSON.parse(locationList);
      setRecentLocation(list);
    }
  };

  const saveRecentLocation = (location: string) => {
    if (!user.memberId) return;

    const locationForId = recentLocation?.[user.memberId] ?? [];
    const filteredLocation = locationForId.filter((loc) => loc !== location);
    const updatedLocationForId = [location, ...filteredLocation].slice(0, 3);

    const updatedLocation: VoteRecentInfo = {
      ...recentLocation,
      [user.memberId]: updatedLocationForId,
    };

    setRecentLocation(updatedLocation);
    setLocalValue("recentLocation", JSON.stringify(updatedLocation));
  };

  const isSkeletonDisplay = () => {
    return isBuildingLoading || popup === "differentBuilding";
  };

  const isContentDisplay = () => {
    return (
      selectedFloor &&
      selectedSpace &&
      !isBuildingLoading &&
      popup !== "differentBuilding"
    );
  };

  const isSpaceDisplay = () => {
    if (!selectedSpace?.buildingSpaceId) return true;

    return (
      spaceList.length > 0 &&
      selectedSpace &&
      spaceList.some(
        (item) => item.buildingSpaceId === selectedSpace.buildingSpaceId
      )
    );
  };

  const checkVote = () => {
    if (vote.endedAt) {
      const [date, time] = (
        safeReplaceAll(vote.endedAt, "/", "-") as string
      ).split(" ");
      if (isTimePassed(date, time.substring(0, 5))) {
        setPopup("voteEnd");
      } else {
        setPopup("voting");
      }
    }
  };

  const handleRefresh = (showIsLoading: boolean = true) => {
    if (selectedSpace?.buildingSpaceId) {
      loadSpaceDetail(selectedSpace.buildingSpaceId);
    }

    loadVote({ vote, showIsLoading });
  };

  const resetVoteData = () => {
    setSelectedVote(undefined);
    setSpaceDetail(undefined);
    setVoteSpace(undefined);
    setVote({});
  };

  useEffect(() => {
    if (selectedFloor?.buildingFloorId) {
      setIsBuildingLoading(true);
      getBuildingSpaceList({
        buildingId: paramBuildingId,
        buildingFloorId: selectedFloor.buildingFloorId,
      })
        .then((res) => {
          if (res.status === HttpStatusCode.Ok) {
            setSpaceList(
              res.data?.map((item) => ({
                ...item,
              }))
            );
          }
        })
        .finally(() => {
          setIsBuildingLoading(false);
        });
    }
  }, [selectedFloor?.buildingFloorId]);

  useEffect(() => {
    if (user.isLoading || isVoteLoading || isBuildingLoading) return;
    if (!vote.buildingId) return;
    if (!user.buildingId) return;

    if (user.buildingId !== vote.buildingId) {
      setPopup("differentBuilding");
    }
  }, [user.isLoading, vote.buildingId, user.buildingId]);

  useEffect(() => {
    if (!isLoading) return;

    clearErrors();
  }, [isLoading]);

  useEffect(() => {
    if (user.isLoading) return;

    const apiList: Promise<void>[] = [
      loadNotice(),
      loadFloor(),
      loadCategorySetting(),
    ];
    if (paramVoteId) {
      apiList.push(loadSpaceDetail(paramSpaceId ?? ""));
      apiList.push(loadVote({ vote: { voteId: paramVoteId } }));
    } else {
      apiList.push(loadSpaceDetail(paramSpaceId ?? "", loadVote));
    }

    setIsLoading(true);
    setIsVoteLoading(true);
    Promise.allSettled(apiList).finally(() => {
      setIsLoading(false);
      setIsVoteLoading(false);
    });

    getRecentLocation();
  }, [user.isLoading]);

  return (
    <div>
      {loadingComponent}
      <div className="flex flex-col">
        <StickyHeader
          title="더워요 추워요"
          className="px-5"
          onBackClick={() => navigate("/service/hot-cold-vote")}
        />

        {vote.buildingId && vote.buildingId !== user.buildingId && (
          <DifferentBuildingPopup popup={popup} />
        )}

        <div className="flex flex-col px-5">
          {hotColdNotice && (
            <div
              className="rounded-[3px] py-3 pl-[15px] pr-[20px] bg-[#F7F7F7] text-brand-primary-gray-100 text-xs flex items-center gap-2.5 justify-between"
              onClick={() => navigate(`/talk/${hotColdNotice.lifePostId}`)}
            >
              <div className="flex items-center gap-[5px]">
                <HomeBuildingNoticeIcon fill="#EA27C2" />
                <div className="truncate w-[calc(100vw-110px)]">
                  {hotColdNotice.title}
                </div>
                <ViewNoticeLinkIcon stroke="black" />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2.5 pt-5 pb-5 text-[26px] font-medium">
            <div className="flex gap-2.5">
              {isSkeletonDisplay() || !user.buildingName ? (
                <Skeleton className="h-[38px] w-[calc(100vw-150px)] rounded-lg" />
              ) : (
                <span className="leading-[38px] truncate">
                  {user.buildingName}
                </span>
              )}
              {isSkeletonDisplay() ? (
                <Skeleton className="h-[38px] w-[100px] rounded-lg" />
              ) : (
                <CommonSelect
                  optionList={floorList.map((floor) => ({
                    value: floor.buildingFloorId,
                    label: floor.floorName,
                  }))}
                  defaultValue="층 선택"
                  labelClassName="text-brand-primary-magenta text-[26px]"
                  className="min-w-[70px]"
                  onChange={(option) => {
                    const findItem = floorList.find(
                      (item) => item.buildingFloorId === option.value
                    );
                    setSelectedFloor(findItem);
                    setSelectedSpace(undefined);
                    resetVoteData();
                  }}
                  value={selectedFloor?.buildingFloorId}
                  arrowIcon={<TalkFindBuildingDownArrow />}
                  onClick={() => {
                    removeError("selectFloor");
                    if (floorList.length === 0) {
                      open?.("선택 가능한 층이 없습니다.");
                      return;
                    }
                  }}
                  optionOrder="between"
                  variant="selectBuilding"
                  unUsePadding
                  error={errors["selectFloor"]}
                  ref={selectFloorRef}
                />
              )}
            </div>

            <div
              className="flex flex-wrap gap-2"
              data-testid="hot-cold-vote-detail-status"
            >
              {isSkeletonDisplay() || !isSpaceDisplay() ? (
                <Skeleton className="h-[38px] w-[calc(100vw-100px)] rounded-lg" />
              ) : (
                <>
                  <CommonSelect
                    optionList={spaceList.map((floor) => ({
                      value: floor.buildingSpaceId,
                      label: floor.spaceName,
                    }))}
                    defaultValue="상세공간 선택"
                    labelClassName="text-brand-primary-magenta text-[26px] max-w-fit"
                    onChange={(option) => {
                      const findItem = spaceList.find(
                        (item) => item.buildingSpaceId === option.value
                      );
                      if (!findItem) return;

                      setSelectedSpace(findItem);
                      resetVoteData();
                      loadSpaceDetail(findItem.buildingSpaceId, loadVote);
                    }}
                    value={selectedSpace?.buildingSpaceId}
                    arrowIcon={<TalkFindBuildingDownArrow />}
                    onClick={() => {
                      removeError("selectSpace");
                      if (spaceList.length === 0) {
                        open?.("선택 가능한 상세공간이 없습니다.");
                        return;
                      }
                    }}
                    optionOrder="between"
                    optionWidth="240px"
                    wrapperClassName="w-fit"
                    variant="selectBuilding"
                    unUsePadding
                    error={errors["selectSpace"]}
                    ref={selectSpaceRef}
                  />

                  <span className="leading-[38px] font-light">{`의 ${getVoteStatusLabel(
                    vote.status
                  )}`}</span>
                </>
              )}
            </div>
          </div>

          <PinchZoomImage
            imageList={
              spaceDetail?.planFile?.items
                ?.filter((item) => item.scale === "full")
                .map((file) => file.url) ?? []
            }
            isOpen={isModalOpen}
            onClose={(e: React.MouseEvent) => {
              e.stopPropagation(); // 이벤트 버블링을 중단합니다.
              setIsModalOpen(false);
            }}
          />

          {(spaceDetail?.planFile?.items?.length ?? 0) > 0 && (
            <img
              className="max-h-40 pb-[15px] pt-5"
              src={
                spaceDetail?.planFile?.items?.find(
                  (item) => item.scale === "full"
                )?.url
              }
              alt="space"
              onClick={() => {
                setIsModalOpen(true);
              }}
              data-testid="space-image"
            />
          )}

          {!spaceDetail && isLoading ? (
            <Skeleton className="w-[calc(100vw-40px)] h-[84px] rounded-lg" />
          ) : (
            <HotColdSummaryList
              buildingSpace={{
                temperature: spaceDetail?.temperature,
                humidity: spaceDetail?.humidity,
                equipmentStatus: spaceDetail?.equipmentStatus,
                ventilationStatus: spaceDetail?.ventilationStatus,
              }}
            />
          )}
        </div>
        <HorizontalRule thickness={6} className="bg-brand-neutral-05" />
        {categorySetting?.ableHotColdAfterWorkingTime && (
          <>
            <div
              className="flex justify-between items-center px-5"
              onClick={() =>
                navigate("/report", {
                  state: {
                    serviceTemplateId: SERVICE_TEMPLATE_ID.OFF_WORK_HOT_COLD,
                  },
                })
              }
            >
              <div className=" text-brand-primary-gray-100 text-sm flex items-center gap-1">
                {"업무시간 외 추가 냉/난방 요청"}
              </div>
              <div>
                <RemainBalanceRightArrow />
              </div>
            </div>
            <HorizontalRule thickness={6} className="bg-brand-neutral-05" />
          </>
        )}

        <div
          className="px-5 pt-[10px]"
          data-testid="hot-cold-vote-detail-content"
        >
          {isContentDisplay() && (
            <div
              className="flex flex-col gap-[30px]"
              data-testid="hot-cold-vote-detail-content-is-display"
            >
              {vote?.status === undefined &&
                voteSpace?.vote?.status === undefined && (
                  <>
                    <HotColdLabelWrapper label="투표 발의">
                      <VoteBanner
                        content={`덥거나 추울 때 투표를 시작해 주세요.\n같은 공간을 사용하는 사람들의 의견을 모아 샌디매니저에게 전달합니다.`}
                        status={vote.status}
                        imgClassName="w-[95px] h-[65px]"
                        imgUrl={YesNo}
                      />
                    </HotColdLabelWrapper>
                    <HotColdLabelWrapper
                      label={
                        <>
                          세부위치정보
                          <span className="text-brand-primary-red">(필수)</span>
                        </>
                      }
                    >
                      <div className="flex flex-col gap-2">
                        <CommonInput
                          value={vote?.location ?? ""}
                          onClick={() => {
                            setLocationIndex(-1);
                          }}
                          onChange={(value) => {
                            removeError("inputLocation");
                            setLocationIndex(-1);
                            setVote((prev) => ({
                              ...prev,
                              location: value,
                            }));
                          }}
                          className="text-sm rounded-[3px]"
                          placeholder="예시) 801호 부근"
                          error={errors["inputLocation"]}
                          ref={inputLocationRef}
                        />
                        {user.memberId && recentLocation && (
                          <>
                            <HorizontalRule
                              className="bg-brand-neutral-05"
                              margin="0"
                            />
                            <span className="text-brand-neutral-60 text-sm">
                              최근 입력한 세부위치정보
                            </span>
                            <div className="flex flex-col gap-2.5">
                              {recentLocation[user.memberId]?.map(
                                (location, index) => (
                                  <div
                                    key={index}
                                    className={clsx(
                                      "rounded-[3px] px-4 py-[15.5px] text-sm border",
                                      {
                                        " border-brand-primary-magenta":
                                          index === locationIndex,
                                      }
                                    )}
                                    onClick={() => {
                                      if (locationIndex !== index) {
                                        setVote((prev) => ({
                                          ...prev,
                                          location,
                                        }));
                                        setLocationIndex(index);
                                      } else {
                                        setVote((prev) => ({
                                          ...prev,
                                          location: "",
                                        }));
                                        setLocationIndex(-1);
                                      }
                                      removeError("inputLocation");
                                    }}
                                  >
                                    {location}
                                  </div>
                                )
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </HotColdLabelWrapper>
                    <HotColdLabelWrapper
                      className="gap-5"
                      label={
                        <div className="flex flex-col gap-0.5">
                          <div className="flex gap-2.5 whitespace-pre-line font-medium text-[18px] leading-[21px] items-center">
                            <VoteIcon showBackground />
                            <div>
                              <strong>{user.nickname}</strong>님!
                              {"\n지금 더우신가요 추우신가요?"}
                            </div>
                          </div>
                          <span className="text-brand-neutral-50 text-xs">
                            * 투표는 상세위치당 하루 2번만 발의 가능합니다.
                          </span>
                        </div>
                      }
                    >
                      <div className="flex gap-2.5 justify-center">
                        <VoteButton
                          type="CREATE"
                          voteItemType={VOTE_ITEM_TYPE.HOT}
                          onClick={() => {
                            if (validationVoteCreate()) {
                              setSelectedVote("HOT");
                              setPopup("voteCreate");
                            }
                          }}
                        />
                        <VoteButton
                          type="CREATE"
                          voteItemType={VOTE_ITEM_TYPE.COLD}
                          onClick={() => {
                            if (validationVoteCreate()) {
                              setSelectedVote("COLD");
                              setPopup("voteCreate");
                            }
                          }}
                        />
                      </div>
                    </HotColdLabelWrapper>
                  </>
                )}

              {vote.status === VOTE_STATUS.ONGOING && !vote.userVoteItem && (
                <>
                  <HotColdLabelWrapper label="투표 진행중">
                    <VoteBanner
                      content={
                        <div className="text-black">
                          {vote.createdByNickname ?? ""}
                          <span className="text-brand-primary-gray-100 opacity-60">{`님이\n`}</span>
                          {vote.location ?? ""}{" "}
                          <span className="text-brand-primary-gray-100 opacity-60">{`투표를 개설하여 진행 중입니다.`}</span>
                        </div>
                      }
                      status={vote.status}
                      imgClassName="w-[65px] h-[70px]"
                      imgUrl={YesNoOngoing}
                    />
                  </HotColdLabelWrapper>
                  <HotColdLabelWrapper
                    label={
                      <div className="flex gap-2.5 whitespace-pre-line font-medium text-[18px] leading-[21px] items-center">
                        <VoteIcon showBackground />
                        <div className="flex flex-col gap-[3px]">
                          {`${user.nickname}님, 투표해 주세요!`}
                          <HotColdVoteRemainTime
                            status={vote.status}
                            endTime={safeReplaceAll(
                              vote.endedAt ?? "",
                              "-",
                              "/"
                            )}
                            prefix={"투표 종료 : "}
                            onRefresh={() =>
                              loadVote({ vote, showIsLoading: false })
                            }
                          />
                        </div>
                      </div>
                    }
                  >
                    <div className="flex flex-col gap-2.5 pt-2">
                      <div className="flex gap-2.5 justify-center items-center">
                        <VoteButton
                          type="ONGOING"
                          voteItemType={VOTE_ITEM_TYPE.HOT}
                          onClick={() => {
                            setSelectedVote("HOT");
                            checkVote();
                          }}
                        />
                        <VoteButton
                          type="ONGOING"
                          voteItemType={VOTE_ITEM_TYPE.GOOD}
                          onClick={() => {
                            setSelectedVote("GOOD");
                            checkVote();
                          }}
                        />
                        <VoteButton
                          type="ONGOING"
                          voteItemType={VOTE_ITEM_TYPE.COLD}
                          onClick={() => {
                            setSelectedVote("COLD");
                            checkVote();
                          }}
                        />
                      </div>
                    </div>
                  </HotColdLabelWrapper>
                </>
              )}

              {vote.status === VOTE_STATUS.ONGOING && vote.userVoteItem && (
                <>
                  {selectedVote && (
                    <HotColdLabelWrapper label={`${user.nickname}님은`}>
                      <div className="flex flex-col gap-2.5">
                        <VoteButton
                          type="VOTED"
                          voteItemType={selectedVote}
                          children={
                            <div className="flex flex-col justify-center items-center gap-1.5">
                              <div>
                                <span className={getTextColor(selectedVote)}>
                                  {getVoteItemTypeLabel(selectedVote)}
                                </span>
                                에 투표하셨어요
                              </div>
                              <HotColdVoteRemainTime
                                className="!text-xs !text-brand-primary-gray-100"
                                status={vote.status}
                                endTime={safeReplaceAll(
                                  vote.endedAt ?? "",
                                  "-",
                                  "/"
                                )}
                                prefix={"투표 종료 : "}
                                onRefresh={() =>
                                  loadVote({ vote, showIsLoading: false })
                                }
                              />
                            </div>
                          }
                        />
                      </div>
                    </HotColdLabelWrapper>
                  )}
                  <HotColdLabelWrapper
                    label="실시간 투표 상황"
                    className="pt-2.5"
                    rightContent={
                      <div onClick={() => handleRefresh()}>
                        <RefreshIcon />
                      </div>
                    }
                  >
                    <HotColdVoteBarChart
                      voteSummary={vote?.summary}
                      showLabel
                      size="medium"
                    />
                  </HotColdLabelWrapper>
                </>
              )}

              {vote?.status === VOTE_STATUS.COMPLETED && (
                <>
                  <HotColdLabelWrapper
                    label={`${
                      vote.votePeriod === "AM" ? "오전" : "오후"
                    } 투표 결과`}
                  >
                    <HotColdVoteBarChart
                      voteSummary={vote?.summary}
                      showLabel
                      size="medium"
                    />
                  </HotColdLabelWrapper>
                  <div
                    className="flex flex-col text-sm leading-[18px] p-[20px] break-keep whitespace-pre-wrap rounded-[10px]"
                    style={{ boxShadow: "0px 1px 5px 0px rgba(0, 0, 0, 0.08)" }}
                  >
                    <span className="font-semibold">{getResultString()}</span>
                    {`${
                      vote.feedback ??
                      `쾌적한 실내온도를 위해 샌디매니저에게 투표결과가 전달됩니다!`
                    }`}
                  </div>

                  {vote?.feedback &&
                    vote.userVoteItem &&
                    !vote?.userSatisfactionInfo && (
                      <div className="flex flex-col items-center gap-2 bg-[#F3F3F3] rounded-[10px] px-5 p-3">
                        <span className="whitespace-pre-line flex justify-center text-center break-keep">{`더 좋은 서비스를 위해 만족도 평가를 진행하시겠어요?`}</span>
                        <CommonButton
                          variant="secondary"
                          className="px-4 py-[11px] text-sm w-[150px]"
                          onClick={() => {
                            setPopup("satisfaction");
                          }}
                        >
                          만족도 평가하기
                        </CommonButton>
                      </div>
                    )}
                  {vote?.userSatisfactionInfo && (
                    <HistorySatisfaction
                      satisfaction={{
                        id: vote.userSatisfactionInfo.etcSatisfactionId,
                        score: vote.userSatisfactionInfo.satisfaction,
                        comment: vote.userSatisfactionInfo.comment,
                        createdAt: vote.userSatisfactionInfo.createdAt,
                        createdBy: vote.userSatisfactionInfo.createdBy,
                        createdByName: vote.userSatisfactionInfo.createdByName,
                        createdByThumbnail:
                          vote.userSatisfactionInfo.createdByThumbnail,
                        reply:
                          vote.userSatisfactionInfo.reply?.etcSatisfactionId &&
                          vote.userSatisfactionInfo.reply?.etcSatisfactionId > 0
                            ? ({
                                id: vote.userSatisfactionInfo.reply
                                  ?.etcSatisfactionId,
                                comment:
                                  vote.userSatisfactionInfo.reply?.comment,
                                createdAt:
                                  vote.userSatisfactionInfo.reply?.createdAt,
                                createdBy:
                                  vote.userSatisfactionInfo.reply?.createdBy,
                                createdByName:
                                  INCOMMODITY_SATISFACTION_MANAGER.NAME,
                                createdByThumbnail:
                                  vote.userSatisfactionInfo.reply
                                    ?.createdByThumbnail,
                              } as SatisfactionReplyItem)
                            : undefined,
                      }}
                      type="hotColdVote"
                      serviceId={vote.voteId?.toString() ?? ""}
                      onLoad={handleRefresh}
                      updateDisabled={Boolean(
                        vote.userSatisfactionInfo.reply?.etcSatisfactionId
                      )}
                      open={open}
                      postSatisfaction={async (
                        satisfactionId: number,
                        satisfaction: SatisfactionRequest
                      ) => {
                        await patchEtcSatisfaction(
                          satisfactionId,
                          satisfaction
                        ).finally(() => {
                          handleRefresh();
                        });
                      }}
                    />
                  )}
                  {categorySetting?.ableHotCold && (
                    <div className="flex flex-col gap-1.5">
                      <div className="text-base font-semibold">
                        투표없이 온도조절을 원하세요?
                      </div>
                      <div className="text-brand-neutral-60 text-sm">
                        공간 온도에 관한 불편 사항이 있을 경우 아래 메뉴에서
                        알려주세요.
                      </div>
                      <div className="flex gap-2.5">
                        {[VOTE_ITEM_TYPE.HOT, VOTE_ITEM_TYPE.COLD].map(
                          (voteItemType, index) => {
                            return (
                              <VoteButton
                                key={index}
                                type="CREATE"
                                voteItemType={voteItemType}
                                className="h-[55px]"
                                imgClassName="w-[30px] h-[30px]"
                                isHorizontal
                                onClick={() => {
                                  let serviceTemplateId =
                                    SERVICE_TEMPLATE_ID.HOT_COLD;

                                  setIsLoading(true);
                                  getHolidayCheck({
                                    date: getDateString(new Date()),
                                  })
                                    .then((res) => {
                                      if (res.status === HttpStatusCode.Ok) {
                                        if (
                                          res.data.isHoliday ||
                                          isWeekendOrAfter6PM()
                                        ) {
                                          serviceTemplateId =
                                            SERVICE_TEMPLATE_ID.OFF_WORK_HOT_COLD;
                                        }
                                      }
                                    })
                                    .finally(() => {
                                      setIsLoading(false);
                                    });

                                  navigate("/report", {
                                    state: {
                                      serviceTemplateId,
                                      selectedItemKey: voteItemType,
                                    },
                                  });
                                }}
                              />
                            );
                          }
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <CommonPopup
        isOpen={popup === "voting"}
        onClose={() => setPopup("")}
        padding={0}
      >
        <div className="whitespace-pre-line text-xl break-keep font-medium p-7 leading-[30px]">
          {`${user.nickname} 님\n`}
          <span className={getTextColor(selectedVote)}>
            {getVoteItemTypeLabel(selectedVote)}
          </span>
          {`에 투표하시겠어요?`}
          <div className="text-brand-neutral-50 text-xs leading-5">
            {paramBuildingId !== user.buildingId &&
              `* 현재 근무 위치와 다른 위치의 투표입니다\n`}
            * 투표 진행 시 변경하실 수 없습니다.
          </div>
        </div>
        <div className="flex">
          <CommonButton
            variant="secondary"
            className="rounded-none flex-1 h-bottom-button"
            onClick={() => setPopup("")}
          >
            취소
          </CommonButton>
          <CommonButton
            className="rounded-none flex-1 h-bottom-button"
            onClick={() => {
              if (!vote.voteId) return;

              setIsLoading(true);
              postHotColdVoteItem({
                voteId: vote.voteId,
                itemType: selectedVote as VoteItemType,
              })
                .then((res) => {
                  if (res.status === HttpStatusCode.Ok) {
                    handleRefresh();
                  }
                })
                .catch((e: any) => {
                  if (e?.response?.data?.error) {
                    open(e?.response?.data?.error.replace(". ", ".\n"));
                  } else {
                    open(
                      "투표 중 오류가 발생했습니다.\n관리자에게 문의해주세요."
                    );
                  }
                })
                .finally(() => {
                  setIsLoading(false);
                  setPopup("");
                });
            }}
          >
            투표
          </CommonButton>
        </div>
      </CommonPopup>

      <CommonPopup
        isOpen={popup === "voteCreate"}
        onClose={() => setPopup("")}
        padding={0}
      >
        <div className="whitespace-pre-line break-keep text-xl font-medium p-7 leading-[30px]">
          {`${user.nickname}님!\n`}
          {`${user.buildingName ?? ""}`}
          <span className="text-brand-primary-magenta">
            {` ${selectedFloor?.floorName} ${selectedSpace?.spaceName} `}
          </span>
          {`부근에서 `}
          <span className={clsx("font-semibold", getTextColor(selectedVote))}>
            {getVoteItemTypeLabel(selectedVote)}
          </span>
          {` 투표를 진행할까요?`}
          <div className="text-brand-neutral-50 text-xs">
            {`* 투표는 상세위치당 하루 2번만 발의 가능합니다.`}
          </div>
        </div>
        <div className="flex">
          <CommonButton
            variant="secondary"
            className="rounded-none flex-1 h-bottom-button"
            onClick={() => {
              setPopup("");
            }}
          >
            취소
          </CommonButton>
          <CommonButton
            className="rounded-none flex-1 h-bottom-button"
            onClick={async () => {
              if (
                !(selectedSpace && selectedSpace.buildingSpaceId) ||
                !(vote && vote.location) ||
                !selectedVote
              ) {
                return;
              }

              setIsLoading(true);
              try {
                const holidayRes = await getHolidayCheck({
                  date: getDateString(new Date()),
                });
                if (holidayRes.status === HttpStatusCode.Ok) {
                  if (holidayRes.data.isHoliday || isWeekendOrAfter6PM()) {
                    setPopup("report");
                    return;
                  }
                }

                const statusRes = await getHotColdVoteStatus({
                  buildingSpaceId: selectedSpace.buildingSpaceId,
                });

                if (statusRes.status === HttpStatusCode.Ok) {
                  if (statusRes.data?.vote?.status === VOTE_STATUS.ONGOING) {
                    setPopup("onGoing");
                    setVote({ voteId: statusRes.data?.vote?.voteId });
                    return;
                  } else if (
                    statusRes.data?.vote?.status === VOTE_STATUS.COMPLETED
                  ) {
                    setPopup("voteEnd");
                    return;
                  }
                }

                const voteRes = await postHotColdVote({
                  buildingSpaceId: selectedSpace.buildingSpaceId,
                  location: vote.location?.trim(),
                  itemType: selectedVote,
                  duration: VOTE_DURATION,
                } as HotColdCreateRequest);

                if (voteRes.status === HttpStatusCode.Ok) {
                  loadVote({ vote: { voteId: voteRes.data.voteId } });

                  if (vote.location?.trim()) {
                    saveRecentLocation(vote.location?.trim());
                  }
                  setPopup("");
                  setSelectedVote(undefined);
                }
              } catch (e: any) {
                if (e?.response?.data?.error) {
                  open(e?.response?.data?.error.replace(". ", ".\n"));
                } else {
                  open("투표 생성을 실패했습니다.\n관리자에게 문의해주세요.");
                }
              } finally {
                setIsLoading(false);
              }
            }}
          >
            진행
          </CommonButton>
        </div>
      </CommonPopup>

      <HotColdVoteEndPopup
        popup={popup}
        onClose={() => setPopup("")}
        onConfirm={() => {
          navigate("/service/hot-cold-vote", { replace: true });
          setPopup("");
        }}
        user={user}
        voteSpace={voteSpace}
      />

      <HotColdVoteOnGoingPopup
        popup={popup}
        onClose={() => setPopup("")}
        onMove={() => {
          handleRefresh();
          setPopup("");
        }}
        user={user}
        voteSpace={voteSpace}
      />

      <HotColdVoteReportPopup
        popup={popup}
        nickname={user.nickname}
        onClose={() => setPopup("")}
      />

      <UserSatisfactionPopup
        popup={popup}
        setPopup={(popup) => setPopup(popup as PopupType)}
        setIsLoading={(isLoading) => setIsLoading(isLoading)}
        open={open}
        postSatisfaction={async (satisfaction: SatisfactionRequest) => {
          await postEtcSatisfaction(
            vote.userVoteItem?.voteItemId?.toString() ?? "",
            {
              ...satisfaction,
              serviceName: "hotColdVote",
            }
          );
          handleRefresh();
        }}
      />

      {simplePopupComponent}
    </div>
  );
}

export type HotColdLabelWrapperProps = {
  className?: string;
  children?: React.ReactNode;
  label?: React.ReactNode;
  bottomLabel?: React.ReactNode;
  rightContent?: React.ReactNode;
};

export const HotColdLabelWrapper = ({
  className,
  children,
  label,
  bottomLabel,
  rightContent,
}: HotColdLabelWrapperProps) => {
  return (
    <div className={clsx("flex flex-col gap-[9px]", className)}>
      <div className="flex flex-col gap-[3px]">
        <div className="flex items-center justify-between text-sm">
          <div className="text-base leading-[19px] font-semibold">{label}</div>
          {rightContent}
        </div>
        <div className="text-xs">{bottomLabel}</div>
      </div>
      {children}
    </div>
  );
};

type VoteButtonProps = {
  className?: string;
  imgClassName?: string;
  type: "CREATE" | "ONGOING" | "VOTED";
  voteItemType?: VoteItemType;
  children?: React.ReactNode;
  onClick?: () => void;
  isHorizontal?: boolean;
};

export const VoteButton = ({
  className,
  imgClassName = "w-[60px] h-[60px]",
  type,
  voteItemType,
  children,
  onClick,
  isHorizontal = false,
}: VoteButtonProps) => {
  const bgColor =
    voteItemType === VOTE_ITEM_TYPE.HOT
      ? "bg-[#FFEEF1]"
      : voteItemType === VOTE_ITEM_TYPE.COLD
      ? "bg-[#E4ECFF]"
      : "bg-[#DCEAF0]";
  const textColor =
    voteItemType === VOTE_ITEM_TYPE.HOT
      ? "text-[#EB3F59]"
      : voteItemType === VOTE_ITEM_TYPE.COLD
      ? "text-[#0A78F2]"
      : "text-[#3C6578]";
  const buttonText = getVoteItemTypeLabel(voteItemType);

  const getVoteIcon = (voteItemType?: VoteItemType) => {
    if (voteItemType === VOTE_ITEM_TYPE.HOT) {
      return VoteHot;
    } else if (voteItemType === VOTE_ITEM_TYPE.COLD) {
      return VoteCold;
    } else if (voteItemType === VOTE_ITEM_TYPE.GOOD) {
      return VoteGood;
    } else {
      return "";
    }
  };

  const renderContent = () => {
    return (
      <>
        <img
          className={imgClassName}
          src={getVoteIcon(voteItemType)}
          alt={`vote-${voteItemType ?? "icon"}`}
        />
        <span className={textColor}>{buttonText}</span>
      </>
    );
  };

  return (
    <div
      className={clsx(
        "flex flex-col flex-1 gap-1.5 justify-center items-center rounded-[3px] font-medium text-lg px-1.5 py-5",
        className,
        bgColor
      )}
      onClick={(e) => {
        onClick?.();
      }}
    >
      {type === "CREATE" && (
        <div
          className={clsx("flex justify-center items-center", {
            "flex-col gap-1.5": !isHorizontal,
            "gap-0.5": isHorizontal,
          })}
        >
          {renderContent()}
        </div>
      )}
      {type === "ONGOING" && (
        <>
          <div className="flex flex-col justify-center items-center gap-1.5">
            {renderContent()}
          </div>
          <div
            className={clsx(
              "flex items-center py-2.5 px-5 rounded-[99px] bg-white text-xs"
            )}
          >
            투표하기
          </div>
        </>
      )}
      {type === "VOTED" && <>{children}</>}
    </div>
  );
};

export type DifferentBuildingPopupProps = {
  popup?: string;
  onClose?: () => void;
};

export const DifferentBuildingPopup = ({
  popup,
  onClose,
}: DifferentBuildingPopupProps) => {
  const navigate = useNavigate();

  return (
    <CommonPopup
      isOpen={popup === "differentBuilding"}
      onClose={onClose}
      padding={0}
    >
      <div className="whitespace-pre-line text-xl font-medium p-7 leading-[30px] text-center">
        {`현재 근무지와 투표지 건물이 다릅니다.\n근무지를 확인해주세요`}
      </div>
      <div className="flex">
        <CommonButton
          className="rounded-none flex-1 h-bottom-button"
          onClick={() => navigate("/view")}
        >
          확인
        </CommonButton>
      </div>
    </CommonPopup>
  );
};

export const CheckIcon = ({ fill }: { fill?: string }) => {
  return (
    <svg
      width="21"
      height="21"
      viewBox="0 0 21 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.22 7.29L8.93001 11.59L7.28 9.94C7.19036 9.83532 7.08004 9.7503 6.95597 9.69027C6.83191 9.63025 6.69678 9.59652 6.55906 9.5912C6.42134 9.58588 6.28401 9.60909 6.15568 9.65936C6.02736 9.70964 5.91081 9.78589 5.81335 9.88335C5.7159 9.9808 5.63964 10.0974 5.58937 10.2257C5.53909 10.354 5.51589 10.4913 5.52121 10.6291C5.52653 10.7668 5.56026 10.9019 5.62028 11.026C5.6803 11.15 5.76532 11.2604 5.87 11.35L8.22 13.71C8.31344 13.8027 8.42426 13.876 8.5461 13.9258C8.66794 13.9755 8.7984 14.0008 8.93001 14C9.19234 13.9989 9.44374 13.8947 9.63 13.71L14.63 8.71C14.7237 8.61704 14.7981 8.50644 14.8489 8.38458C14.8997 8.26272 14.9258 8.13201 14.9258 8C14.9258 7.86799 14.8997 7.73728 14.8489 7.61542C14.7981 7.49356 14.7237 7.38296 14.63 7.29C14.4426 7.10375 14.1892 6.99921 13.925 6.99921C13.6608 6.99921 13.4074 7.10375 13.22 7.29ZM10.5 0.5C8.52219 0.5 6.58879 1.08649 4.9443 2.1853C3.29981 3.28412 2.01809 4.8459 1.26121 6.67317C0.504333 8.50043 0.306299 10.5111 0.692152 12.4509C1.078 14.3907 2.03041 16.1725 3.42894 17.5711C4.82746 18.9696 6.60929 19.922 8.5491 20.3079C10.4889 20.6937 12.4996 20.4957 14.3268 19.7388C16.1541 18.9819 17.7159 17.7002 18.8147 16.0557C19.9135 14.4112 20.5 12.4778 20.5 10.5C20.5 9.18678 20.2413 7.88642 19.7388 6.67317C19.2363 5.45991 18.4997 4.35752 17.5711 3.42893C16.6425 2.50035 15.5401 1.76375 14.3268 1.2612C13.1136 0.758658 11.8132 0.5 10.5 0.5ZM10.5 18.5C8.91775 18.5 7.37104 18.0308 6.05544 17.1518C4.73985 16.2727 3.71447 15.0233 3.10897 13.5615C2.50347 12.0997 2.34504 10.4911 2.65372 8.93928C2.9624 7.38743 3.72433 5.96197 4.84315 4.84315C5.96197 3.72433 7.38743 2.9624 8.93928 2.65372C10.4911 2.34504 12.0997 2.50346 13.5615 3.10896C15.0233 3.71447 16.2727 4.73984 17.1518 6.05544C18.0308 7.37103 18.5 8.91775 18.5 10.5C18.5 12.6217 17.6572 14.6566 16.1569 16.1569C14.6566 17.6571 12.6217 18.5 10.5 18.5Z"
        fill={fill}
      />
    </svg>
  );
};

export type VoteBannerProps = {
  content: React.ReactNode;
  status?: VoteStatus;
  imgUrl?: string;
  imgClassName?: string;
};
export const VoteBanner = ({
  content,
  status,
  imgUrl,
  imgClassName,
}: VoteBannerProps) => {
  return (
    <div
      className={clsx(
        "flex gap-1 justify-between items-center rounded-[10px] pl-[24px] pr-[20px] py-[18.5px] ",
        {
          "bg-[#E9F6FB]": status === undefined,
          "bg-[#F0F5F7]": status === VOTE_STATUS.ONGOING,
        }
      )}
    >
      <div className="text-[18px] leading-[21px] font-medium text-brand-primary-gray-100 whitespace-pre-wrap break-keep">
        {content}
      </div>
      {imgUrl && (
        <div className=" flex-shrink-0">
          <img src={imgUrl} alt={imgUrl} className={imgClassName} />
        </div>
      )}
    </div>
  );
};
